<template>
  <div>
    <label
      v-if="label"
      :for="name"
      class="mb-1 block text-xs font-medium text-background-fg/70"
    >
      {{ label }}
    </label>
    <div class="relative mt-1 rounded-md shadow-sm">
      <input
        :type="type_"
        :name="name"
        :class="[
          'block w-full rounded-md border border-background-fg/8 pr-10 text-base font-semibold focus:outline-none', // Font size set at >14px (text-base) so no zoom on mobile when focused
          hasError
            ? 'border-error text-error placeholder:text-error focus:border-error focus:ring-error'
            : 'focus:border-primary focus:ring-primary',
        ]"
        :placeholder="placeholder"
        :value="inputValue"
        :aria-invalid="hasError"
        :maxlength="maxLength"
        enterKeyHint="done"
        :disabled="disabled"
        v-bind="extraInputAttributes"
        :autocomplete="
          type === 'pin' ? 'off' : autocomplete
        "
        @change="handleChange"
        @blur="handleBlur"
      />
      <div
        class="absolute inset-y-0 right-0 flex items-center pr-3"
      >
        <button
          type="button"
          class="flex items-center"
          tabindex="-1"
          @click="toggleVisible"
        >
          <template v-if="isVisible">
            <IconEye />
          </template>
          <template v-else>
            <IconEyeOff />
          </template>
        </button>
      </div>
    </div>
    <template v-if="hasError">
      <p class="mt-1 text-xs font-medium text-error">
        {{ errorMessage }}
      </p>
    </template>
  </div>
</template>

<script setup lang="ts">
import { useField } from "vee-validate"
import type { InputHTMLAttributes } from "vue"

const props = withDefaults(
  defineProps<{
    modelValue?: string
    name: string
    label: string
    placeholder?: string
    disabled?: boolean
    type?: "password" | "pin"
    autocomplete?: string
    maxLength?: number
  }>(),
  {
    modelValue: "",
    type: "password",
    placeholder: "",
    disabled: false,
    autocomplete: "off",
  }
)

const name = toRef(props, "name")
type InputType = "password" | "text"

const type_ = computed<InputType>(() =>
  isVisible.value ? "text" : "password"
)
const isVisible = ref(false)
const toggleVisible = () => {
  isVisible.value = !isVisible.value
}
const {
  value: inputValue,
  errorMessage,
  handleBlur,
  handleChange,
} = useField(name, undefined, {
  initialValue: props.modelValue,
})

const hasError = computed(() => !!errorMessage.value)

const extraInputAttributes = computed<InputHTMLAttributes>(
  () =>
    props.type === "pin"
      ? {
          pattern: "[0-9]*",
          inputmode: "numeric",
        }
      : {}
)
</script>
